import type { MermaidElementModel } from '@blocksuite/affine-model';
import type {
  MermaidRenderOutput,
  MermaidRenderSize,
} from '@blocksuite/affine-shared/services';
import { type Signal, signal } from '@preact/signals-core';

import type { MermaidInteractionSnapshot } from './interaction-geometry.js';

export type MermaidTransientRenderStatus =
  | 'idle'
  | 'pending'
  | MermaidRenderOutput['status'];

export type MermaidTransientRenderState = {
  interaction: MermaidInteractionSnapshot | null;
  intrinsicSize: MermaidRenderSize | null;
  lastValidSource: string | null;
  message: string | null;
  source: string;
  status: MermaidTransientRenderStatus;
  svg: string | null;
};

const intrinsicSizeSignals = new WeakMap<
  MermaidElementModel,
  Signal<MermaidRenderSize | null>
>();
const renderStateSignals = new WeakMap<
  MermaidElementModel,
  Signal<MermaidTransientRenderState>
>();

function getIntrinsicSizeSignal(model: MermaidElementModel) {
  let size$ = intrinsicSizeSignals.get(model);
  if (!size$) {
    size$ = signal<MermaidRenderSize | null>(null);
    intrinsicSizeSignals.set(model, size$);
  }
  return size$;
}

export function getMermaidIntrinsicSize(model: MermaidElementModel) {
  return getIntrinsicSizeSignal(model).value;
}

export function setMermaidIntrinsicSize(
  model: MermaidElementModel,
  size?: MermaidRenderSize | null
) {
  if (
    !size ||
    !Number.isFinite(size.width) ||
    !Number.isFinite(size.height) ||
    size.width <= 0 ||
    size.height <= 0
  ) {
    return;
  }

  getIntrinsicSizeSignal(model).value = size;
}

function initialRenderState(
  model: MermaidElementModel
): MermaidTransientRenderState {
  return {
    interaction: null,
    intrinsicSize: getMermaidIntrinsicSize(model),
    lastValidSource: null,
    message: null,
    source: model.source.toString(),
    status: 'idle',
    svg: null,
  };
}

export function getMermaidRenderStateSignal(model: MermaidElementModel) {
  let state$ = renderStateSignals.get(model);
  if (!state$) {
    state$ = signal(initialRenderState(model));
    renderStateSignals.set(model, state$);
  }
  return state$;
}

export function setMermaidRenderPending(
  model: MermaidElementModel,
  source: string
) {
  const state$ = getMermaidRenderStateSignal(model);
  state$.value = {
    ...state$.value,
    interaction: null,
    source,
    status: 'pending',
  };
}

export function setMermaidRenderResult(
  model: MermaidElementModel,
  source: string,
  result: MermaidRenderOutput
) {
  const state$ = getMermaidRenderStateSignal(model);
  const current = state$.value;

  if ('intrinsicSize' in result) {
    setMermaidIntrinsicSize(model, result.intrinsicSize);
  }

  switch (result.status) {
    case 'ready':
    case 'degraded':
      state$.value = {
        interaction: null,
        intrinsicSize: result.intrinsicSize ?? current.intrinsicSize,
        lastValidSource: source,
        message: null,
        source,
        status: result.status,
        svg: result.svg,
      };
      return;
    case 'invalid':
    case 'error':
      state$.value = {
        ...current,
        interaction: null,
        intrinsicSize: result.intrinsicSize ?? current.intrinsicSize,
        message: result.message,
        source,
        status: result.status,
        svg: result.svg ?? current.svg,
      };
      return;
    case 'empty':
      state$.value = {
        ...current,
        interaction: null,
        message: null,
        source,
        status: 'empty',
      };
      return;
    case 'deferred':
    case 'superseded':
      state$.value = {
        ...current,
        interaction: null,
        source,
        status: result.status,
      };
  }
}

export function getMermaidInteractionSnapshot(model: MermaidElementModel) {
  return getMermaidRenderStateSignal(model).value.interaction;
}

export function setMermaidInteractionSnapshot(
  model: MermaidElementModel,
  snapshot: MermaidInteractionSnapshot | null
) {
  const state$ = getMermaidRenderStateSignal(model);
  if (snapshot && state$.value.source !== snapshot.source) return;
  state$.value = {
    ...state$.value,
    interaction: snapshot,
  };
}
